import * as React from 'react';
import { Button, Dropdown, Input, MessageBar, MessageBarBody, Option } from '@fluentui/react-components';
import { AddRegular, DeleteRegular } from '@fluentui/react-icons';
import { STAGES } from '../../api/src/programs';
import type { IInstalment, IStaff } from '../../api/src/workspace';
import { errorText, useApp } from '../app/context';

/** The firm: who works on files, and the fee instalments it bills at stages (reminders only). */
export default function FirmSettings(props: { part?: 'team' | 'instalments' } = {}): React.ReactElement | null {
  const app = useApp();
  const w = app.workspace;
  const [staff, setStaff] = React.useState<IStaff[]>(() => w.firm?.staff ?? []);
  const [instalments, setInstalments] = React.useState<IInstalment[]>(() => w.firm?.instalments ?? []);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  if (!w.firm) {
    return null;
  }
  const named: IStaff[] = staff.filter(s => s.name.trim());
  const changed: boolean = JSON.stringify(named) !== JSON.stringify(w.firm.staff) || JSON.stringify(instalments.filter(i => i.label.trim())) !== JSON.stringify(w.firm.instalments ?? []);

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(undefined);
    try {
      await app.saveWorkspace({ ...w, firm: { ...w.firm!, staff: named, instalments: instalments.filter(i => i.label.trim()) } });
      app.toast({ text: 'Saved.' });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };
  const updateStaff = (i: number, c: Partial<IStaff>): void => setStaff(l => l.map((s, j) => (j === i ? { ...s, ...c } : s)));
  const updateInst = (i: number, c: Partial<IInstalment>): void => setInstalments(l => l.map((s, j) => (j === i ? { ...s, ...c } : s)));

  return (
    <section className="card" aria-labelledby="firm-h" style={{ gap: 12 }}>
      <h2 id="firm-h">Your firm</h2>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {props.part !== 'instalments' && <>
      <strong style={{ fontSize: 14 }}>Lawyers and clerks</strong>
      {staff.map((s, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1.4fr) 130px auto', gap: 8, alignItems: 'center' }}>
          <Input aria-label="Name" value={s.name} onChange={(_, d) => updateStaff(i, { name: d.value })} />
          <Input aria-label="Email" placeholder="Email (optional)" value={s.email ?? ''} onChange={(_, d) => updateStaff(i, { email: d.value || undefined })} />
          <Dropdown aria-label="Role" value={s.role === 'lawyer' ? 'Lawyer' : 'Clerk'} selectedOptions={[s.role]} onOptionSelect={(_, d) => updateStaff(i, { role: d.optionValue === 'lawyer' ? 'lawyer' : 'clerk' })} style={{ minWidth: 0 }}>
            <Option value="lawyer">Lawyer</Option><Option value="clerk">Clerk</Option>
          </Dropdown>
          <Button appearance="subtle" icon={<DeleteRegular />} aria-label="Remove" onClick={() => setStaff(l => l.filter((_, j) => j !== i))} />
        </div>
      ))}
      <Button icon={<AddRegular />} onClick={() => setStaff(l => [...l, { name: '', role: 'clerk' }])} style={{ alignSelf: 'flex-start' }}>Add a person</Button>
      </>}
      {props.part !== 'team' && <>

      <strong style={{ fontSize: 14 }}>Fee instalments</strong>
      <span className="small" style={{ fontSize: 14 }}>When a file reaches a stage, a reminder to bill the instalment shows on Today and on the file. Billing itself stays in your accounting.</span>
      {instalments.map((x, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) 220px auto', gap: 8, alignItems: 'center' }}>
          <Input aria-label="Instalment" placeholder="e.g. Second instalment" value={x.label} onChange={(_, d) => updateInst(i, { label: d.value })} />
          <Dropdown aria-label="When the file reaches" value={STAGES.filter(s => s.id === x.stage)[0]?.label ?? ''} selectedOptions={[x.stage]} onOptionSelect={(_, d) => updateInst(i, { stage: d.optionValue ?? 'submitted' })} style={{ minWidth: 0 }}>
            {STAGES.map(s => <Option key={s.id} value={s.id}>{s.label}</Option>)}
          </Dropdown>
          <Button appearance="subtle" icon={<DeleteRegular />} aria-label="Remove" onClick={() => setInstalments(l => l.filter((_, j) => j !== i))} />
        </div>
      ))}
      <Button icon={<AddRegular />} disabled={instalments.length >= 10} onClick={() => setInstalments(l => [...l, { label: '', stage: 'submitted' }])} style={{ alignSelf: 'flex-start' }}>Add an instalment</Button>
      </>}
      <Button appearance="primary" disabled={saving || !changed || !named.some(s => s.role === 'lawyer')} onClick={() => { void save(); }} style={{ alignSelf: 'flex-start' }}>{saving ? 'Saving…' : 'Save'}</Button>
    </section>
  );
}
